type QueryLike = Record<string, string | string[] | undefined>;

export interface SlugFilterConfig {
  readonly slugs: readonly string[];
  filterToSlug: (editValues: Record<string, unknown>) => string | null;
  slugToFilter: (slug?: string) => Record<string, unknown>;
  redirectFromQuery: (rawValue: string) => string | null;
  readonly handledKeys: readonly string[];
}

export const FilterSlugs = {
  DAILY_RENTAL: 'gunluk',
  LONG_TERM_RENTAL: 'uzunmuddetli'
} as const;

export const LeasedSlugs = {
  RENT: 'kiraye',
  SALE: 'alqi-satqi'
} as const;

type FilterSlugValue = (typeof FilterSlugs)[keyof typeof FilterSlugs];

const PAID_DAILY_SLUG_MAP: ReadonlyArray<readonly [boolean, FilterSlugValue]> =
  [
    [true, FilterSlugs.DAILY_RENTAL],
    [false, FilterSlugs.LONG_TERM_RENTAL]
  ];

function defineSlugFilter<T>(params: {
  editKey: string;
  filterKey: string;
  slugMap: ReadonlyArray<readonly [T, string]>;
  condition?: (editValues: Record<string, unknown>) => boolean;
  handledKeys: readonly string[];
  parseQueryValue: (raw: string) => T | undefined;
}): SlugFilterConfig {
  const {
    editKey,
    filterKey,
    slugMap,
    condition,
    handledKeys,
    parseQueryValue
  } = params;
  return {
    slugs: slugMap.map(([, slug]) => slug),
    filterToSlug: editValues => {
      if (condition && !condition(editValues)) return null;
      const val = editValues[editKey] as T | undefined;
      return slugMap.find(([v]) => v === val)?.[1] ?? null;
    },
    slugToFilter: slug => {
      const entry = slugMap.find(([, s]) => s === slug);
      return entry ? { [filterKey]: entry[0] } : {};
    },
    redirectFromQuery: rawValue => {
      const parsed = parseQueryValue(rawValue);
      if (parsed === undefined) return null;
      return slugMap.find(([v]) => v === parsed)?.[1] ?? null;
    },
    handledKeys
  };
}

export const SLUG_FILTER_CONFIGS: readonly SlugFilterConfig[] = [
  defineSlugFilter<boolean>({
    editKey: 'paid_daily',
    filterKey: 'paidDaily',
    condition: ev => ev.leased === true,
    slugMap: PAID_DAILY_SLUG_MAP,
    handledKeys: ['paid_daily'],
    parseQueryValue: raw => {
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return undefined;
    }
  })
];

export const SLUG_FILTER_REGEX = `(${SLUG_FILTER_CONFIGS.flatMap(c => c.slugs).join('|')})`;

export function buildRedirectPath(
  query: QueryLike,
  searchSegments: string[]
): string | null {
  const allSlugs = new Set(SLUG_FILTER_CONFIGS.flatMap(c => c.slugs));
  if (searchSegments.some(seg => allSlugs.has(seg))) return null;
  if (!searchSegments.includes(LeasedSlugs.RENT)) return null;

  const candidates = SLUG_FILTER_CONFIGS.flatMap(config =>
    config.handledKeys.map(key => {
      const rawValue = query[key];
      const slug =
        rawValue !== undefined
          ? config.redirectFromQuery(String(rawValue))
          : null;
      return { config, key, slug };
    })
  );

  const match = candidates.find(({ slug }) => slug !== null);
  if (!match || match.slug === null) return null;

  const { config, slug } = match;
  const searchPath = searchSegments.join('/');
  const excluded = new Set([...config.handledKeys, 'search', 'url']);
  const params = new URLSearchParams();

  Object.entries(query).forEach(([k, v]) => {
    if (excluded.has(k)) return;
    if (Array.isArray(v)) v.forEach(val => params.append(k, val));
    else if (v !== undefined) params.set(k, v);
  });

  const queryStr = params.toString() ? `?${params.toString()}` : '';
  return `/${searchPath}/${slug}${queryStr}`;
}
